import { useState, type Dispatch, type SetStateAction } from 'react';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { safeAxios } from '@/services/safeAxios';
import { Box } from '@/UI/Box/Box';
import { Skeleton } from '@/UI/Skeleton/Skeleton';
import { log } from '@/logger/logger.ts';
import { color } from '@/colors';
import { appendQsFromArray } from '@/utils/urls';
import { FlexCenHorVer, FlexColCenHorVer } from '@/UI/styles';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { Media64Icon } from '@/UI/Icons/Media64Icon';
import { ToolOutputHistory } from '@/components/Tool/ToolOutputHistory';
import DesignAppGallery from '../DesignAppGallery';
import type { ToolOutputRemovalTarget } from '@/components/Tool/ToolOutputActions';
import type { ApiPath } from '@/services/apiPath';
import type { DeleteFunctions } from '@/components/Common/ImageList/types';
import type { RenderingAsset, MediaAsset, UploadedAsset } from '@/types/api/assets';
import type { DesignAppResult } from '@/types/design-app.types';

const logger = log.getLogger('Preview');

interface PreviewProps {
  resultsUrl: ApiPath;
  version: string;
  examples?: UploadedAsset[];
  isLoading: boolean;
  isProcessing: boolean;
  results: DesignAppResult[];
  setResults: Dispatch<SetStateAction<DesignAppResult[]>>;
  recipeName: string;
  numberOfLoadingResults: number;
  progress?: number;
  layout?: 'carousel' | 'masonry';
}

const Preview = ({
  resultsUrl,
  version,
  examples = [],
  isLoading,
  isProcessing,
  results,
  setResults,
  recipeName,
  numberOfLoadingResults,
  progress,
  layout = 'carousel',
}: PreviewProps) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const isMasonry = layout === 'masonry' && (results.length > 0 || isProcessing || isLoading);
  const [selected, setSelected] = useState(0);

  /** Removes results on the server, then from `results`. Rejects, changing nothing, when the server fails. */
  const removeResults = async (targets: readonly ToolOutputRemovalTarget[]) => {
    const params = appendQsFromArray('assetIds', [...new Set(targets.map(({ id }) => id))]);
    params.append('version', version);
    await safeAxios.delete(resultsUrl, { params });
    setResults((current) =>
      current.filter((result) => !targets.some(({ id, runId }) => result.id === id && result.runId === runId)),
    );
  };

  const deleteOutput = async (target: ToolOutputRemovalTarget) => {
    if (numberOfLoadingResults > 0) {
      return false;
    }
    try {
      await removeResults([target]);
      return true;
    } catch (e) {
      logger.error('deleteCurrentResult(): error', e);
      return false;
    }
  };

  const deleteCurrentResult = async () => {
    const target = results[selected];
    if (target && (await deleteOutput(target))) {
      setSelected(0);
    }
  };

  const deleteAllOtherResults = async () => {
    if (numberOfLoadingResults > 0) {
      // will delete incorrectly while rendering new results
      return;
    }
    try {
      const idsToDelete = results.filter((_, index) => index !== selected).map((result) => result.id);
      const params = appendQsFromArray('assetIds', idsToDelete);
      params.append('version', version);
      await safeAxios.delete(resultsUrl, { params });
      setResults(results.filter((_, index) => index === selected));
      setSelected(0);
    } catch (e) {
      logger.error('deleteAllOtherResults(): error', e);
    }
  };

  const deleteAllResults = async () => {
    try {
      const idsToDelete = results.map((result) => result.id);
      const params = appendQsFromArray('assetIds', idsToDelete);
      params.append('version', version);
      await safeAxios.delete(resultsUrl, { params });
      setResults([]);
      setSelected(0);
    } catch (e) {
      logger.error('deleteAllResults(): error', e);
    }
  };

  const deletionFunctions: DeleteFunctions = {
    deleteCurrentResult,
    deleteAllOtherResults,
    deleteAllResults,
    disableDelete: numberOfLoadingResults > 0,
  };

  if (isMasonry) {
    return (
      <ToolOutputHistory
        results={results}
        recipeName={recipeName}
        onRemoveResults={removeResults}
        disableRemove={isProcessing || isLoading || numberOfLoadingResults > 0}
        isLoading={isProcessing || isLoading}
        progress={progress}
      />
    );
  }

  return (
    <FlexCenHorVer
      id="design-app-preview-container"
      sx={{
        flex: 1,
        background: isFplMigrationEnabled ? cssVars.color.bg : color.Black92,
        border: `1px solid ${color.Dark_Grey}`,
        p: 2,
        borderRadius: 3,
      }}
    >
      <FlexCenHorVer
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        {isLoading ? (
          <FlexColCenHorVer
            sx={{
              width: '100%',
              height: '80%',
              gap: 4,
            }}
          >
            <Box
              sx={{
                flex: 1,
                width: '80%',
                position: 'relative',
              }}
            >
              <Skeleton variant="rounded" animation="wave" width="100%" height="100%" />
              <Media64Icon
                width={64}
                height={64}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: token.textSecondary,
                }}
              />
            </Box>
            <FlexCenHorVer
              sx={{
                width: '80%',
                height: '20%',
                gap: 2,
                position: 'relative',
              }}
            >
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} variant="rounded" animation="wave" width="100%" height="100%" />
              ))}
            </FlexCenHorVer>
          </FlexColCenHorVer>
        ) : (
          <>
            {results.length > 0 || isProcessing ? (
              <DesignAppGallery
                recipeName={recipeName}
                mediaArray={
                  isProcessing
                    ? [
                        ...Array<MediaAsset | RenderingAsset>(numberOfLoadingResults).fill({
                          type: 'rendering',
                          progress,
                        }),
                        ...results,
                      ]
                    : results
                }
                selected={selected}
                setSelected={setSelected}
                deletionFunctions={deletionFunctions}
              />
            ) : examples.length > 0 ? (
              <DesignAppGallery
                recipeName={recipeName}
                mediaArray={examples}
                selected={selected}
                setSelected={setSelected}
              />
            ) : (
              <FlexCenHorVer
                sx={{
                  border: `1px solid ${color.White64_T}`,
                  borderRadius: 2,
                  width: '100%',
                  height: '100%',
                }}
              >
                <Media64Icon width={64} height={64} />
              </FlexCenHorVer>
            )}
          </>
        )}
      </FlexCenHorVer>
    </FlexCenHorVer>
  );
};

export default Preview;
